import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── MICRO TRIGGERS ───
const MICRO_TRIGGERS = [
  "Az önce bir şey fark ettin… çoğu kişi göremez bunu.",
  "Burası genelde insanların geri döndüğü nokta.",
  "Sen hâlâ buradasın. Bu bir tesadüf değil.",
  "Bu aşamaya kadar gelen %8'in içindesin.",
  "Zihnin şu an direniyor. Bu iyi bir işaret.",
  "Dikkat et… sonraki adım her şeyi değiştirebilir.",
];
const getRandomTrigger = () => MICRO_TRIGGERS[Math.floor(Math.random() * MICRO_TRIGGERS.length)];

// ─── VISITOR ID ───
const getVisitorId = () => {
  let id = localStorage.getItem('zr_visitor_id');
  if (!id) {
    id = 'zr_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('zr_visitor_id', id);
  }
  return id;
};

// ═══════════════════════════════════════════
// PREMIUM SUB-COMPONENTS
// ═══════════════════════════════════════════

// [6] Glowing cursor typewriter
const Typewriter = ({ text, delay = 35, onComplete, className = '' }) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayed(text.slice(0, i + 1));
        i++;
      } else {
        setDone(true);
        clearInterval(timer);
        onComplete?.();
      }
    }, delay);
    return () => clearInterval(timer);
  }, [text, delay]);

  return (
    <span className={className}>
      {displayed}
      {!done && (
        <span
          className="inline-block w-[2px] h-[1.1em] ml-[2px] align-middle rounded-full animate-zrCursorGlow"
          style={{ background: 'linear-gradient(to bottom, rgba(168,85,247,0.8), rgba(236,72,153,0.5))' }}
        />
      )}
    </span>
  );
};

// [7] Breathing dots for silent pause
const BreathingDots = () => (
  <div className="flex items-center justify-center gap-2 h-12">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="block w-1.5 h-1.5 rounded-full bg-purple-400/50 animate-zrDotPulse"
        style={{ animationDelay: `${i * 0.3}s` }}
      />
    ))}
  </div>
);

const SilentPause = ({ duration = 1800, onComplete }) => {
  useEffect(() => {
    const t = setTimeout(onComplete, duration);
    return () => clearTimeout(t);
  }, [duration, onComplete]);
  return <BreathingDots />;
};

// [3] Breathing radial glow background
const BreathingBackground = () => (
  <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
    <div
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vh] rounded-full animate-zrBreathe"
      style={{ background: 'radial-gradient(ellipse, rgba(168,85,247,0.08) 0%, rgba(120,50,200,0.03) 40%, transparent 70%)' }}
    />
    <div
      className="absolute top-[30%] left-[60%] w-[40vw] h-[40vh] rounded-full animate-zrBreathe"
      style={{ background: 'radial-gradient(ellipse, rgba(236,72,153,0.04) 0%, transparent 60%)', animationDelay: '-4s' }}
    />
    <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />
  </div>
);

// [2] Staggered text container
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
  visible: {
    opacity: 1, y: 0, filter: 'blur(0px)',
    transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }
  }
};

// [1] Cinematic step transition variants
const sceneVariants = {
  initial: { opacity: 0, scale: 1.015, filter: 'blur(6px)' },
  animate: { opacity: 1, scale: 1, filter: 'blur(0px)', transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, scale: 0.985, filter: 'blur(4px)', transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

// Depth indicator
const StepContainer = ({ children, depth = 0, maxDepth = 13 }) => (
  <div className="relative min-h-screen flex items-center justify-center px-6">
    <div className="fixed left-0 top-0 bottom-0 w-[2px] z-50">
      <motion.div
        className="w-full"
        style={{ background: 'linear-gradient(to bottom, rgba(168,85,247,0.5), rgba(236,72,153,0.3))' }}
        initial={{ height: '0%' }}
        animate={{ height: `${(depth / maxDepth) * 100}%` }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />
    </div>
    <div className="max-w-xl w-full mx-auto py-20 relative z-10">{children}</div>
  </div>
);

// [8] Glass-morphism choice button
const ChoiceButton = ({ label, selected, onClick, testId }) => (
  <motion.button
    onClick={onClick}
    className={`w-full text-left px-6 py-4 rounded-xl border transition-all duration-500 relative overflow-hidden group ${
      selected
        ? 'bg-purple-500/10 border-purple-500/30 text-white shadow-[0_0_20px_rgba(168,85,247,0.1)] animate-zrGlowRing'
        : 'bg-white/[0.03] border-white/[0.1] text-white/70 hover:text-white/90'
    }`}
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.98 }}
    data-testid={testId}
  >
    <div className={`absolute inset-0 transition-opacity duration-500 ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.05) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)', backdropFilter: 'blur(8px)' }}
    />
    <span className="relative z-10 text-base font-light tracking-wide">{label}</span>
  </motion.button>
);

// Continue button with premium glow
const ContinueBtn = ({ onClick, label = "Devam Et", disabled = false, testId }) => (
  <motion.button
    onClick={onClick}
    disabled={disabled}
    className="mt-8 group inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-purple-600/20 border border-purple-400/30 text-white/90 text-sm font-medium transition-all duration-500 disabled:opacity-20 disabled:cursor-not-allowed hover:bg-purple-600/30 hover:border-purple-400/50"
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testId}
  >
    <span className="tracking-wider">{label}</span>
    <div className="relative">
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
    </div>
  </motion.button>
);

// [5] Whisper text
const Whisper = ({ children, className = '' }) => (
  <motion.p variants={staggerItem} className={`text-base sm:text-lg text-white/80 font-light tracking-[0.02em] leading-[1.7] font-outfit ${className}`}>
    {children}
  </motion.p>
);

// [4+5] Impact text — clean, readable, premium
const Impact = ({ children, className = '', italic = false }) => (
  <motion.p variants={staggerItem} className={`text-xl sm:text-2xl text-white font-semibold leading-snug tracking-tight font-outfit ${italic ? 'italic text-purple-100/80' : ''} ${className}`}>
    {children}
  </motion.p>
);

// Divider
const Divider = () => (
  <motion.div variants={staggerItem} className="flex items-center gap-3 my-8">
    <div className="h-px flex-1 max-w-[60px]" style={{ background: 'linear-gradient(to right, transparent, rgba(168,85,247,0.25), transparent)' }} />
  </motion.div>
);

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════
export default function ZihinRehberi({ isOpen, onClose }) {
  const [step, setStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState({ problem: null, experience: null, urgency: null });
  const [userName, setUserName] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [typewriterDone, setTypewriterDone] = useState(false);
  const [showContinue, setShowContinue] = useState(false);
  const [microTrigger, setMicroTrigger] = useState('');
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [capacity, setCapacity] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const navigate = useNavigate();
  const sessionCreated = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API}/api/masterclass/settings/public`)
      .then(r => r.json())
      .then(d => { if (d.capacity) setCapacity(d.capacity); })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const visitorId = getVisitorId();
    const savedName = localStorage.getItem('zr_user_name');
    if (savedName) setUserName(savedName);

    const savedStep = localStorage.getItem('zr_last_step');
    const savedAnswers = localStorage.getItem('zr_answers');
    if (savedStep && parseInt(savedStep) > 1) {
      setResumeData({
        step: parseInt(savedStep),
        answers: savedAnswers ? JSON.parse(savedAnswers) : {},
        name: savedName || ''
      });
      setShowResumePrompt(true);
      return;
    }

    if (!sessionCreated.current) createNewSession(visitorId);
  }, [isOpen]);

  const createNewSession = async (visitorId) => {
    try {
      const res = await fetch(`${API}/api/zihin-rehberi/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId })
      });
      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);
      sessionCreated.current = true;
    } catch (e) { console.error(e); }
  };

  const handleResume = () => {
    if (resumeData) {
      setStep(resumeData.step);
      setAnswers(resumeData.answers);
      if (resumeData.name) setUserName(resumeData.name);
    }
    setShowResumePrompt(false);
    if (!sessionCreated.current) createNewSession(getVisitorId());
  };

  const handleFreshStart = () => {
    localStorage.removeItem('zr_last_step');
    localStorage.removeItem('zr_answers');
    setResumeData(null);
    setShowResumePrompt(false);
    setStep(0);
    setAnswers({ problem: null, experience: null, urgency: null });
    if (!sessionCreated.current) createNewSession(getVisitorId());
  };

  const updateSession = useCallback(async (updates) => {
    if (!sessionId) return;
    try {
      await fetch(`${API}/api/zihin-rehberi/session/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
    } catch (e) { console.error(e); }
  }, [sessionId]);

  const handleClose = () => {
    if (step > 1 && !showExitIntent) {
      setShowExitIntent(true);
      return;
    }
    const stepNames = ['hook','name','permission','q1','q2','q3','mirror','decision','masterclass','post_mc','selection','training','community','close'];
    updateSession({ dropped_at_step: stepNames[step] || 'unknown' });
    localStorage.setItem('zr_last_step', String(step));
    localStorage.setItem('zr_answers', JSON.stringify(answers));
    sessionCreated.current = false;
    setStep(0);
    setAnswers({ problem: null, experience: null, urgency: null });
    setTypewriterDone(false);
    setShowContinue(false);
    setShowExitIntent(false);
    onClose();
  };

  const forceClose = () => {
    setShowExitIntent(false);
    const stepNames = ['hook','name','permission','q1','q2','q3','mirror','decision','masterclass','post_mc','selection','training','community','close'];
    updateSession({ dropped_at_step: stepNames[step] || 'unknown' });
    localStorage.setItem('zr_last_step', String(step));
    localStorage.setItem('zr_answers', JSON.stringify(answers));
    sessionCreated.current = false;
    setStep(0);
    setAnswers({ problem: null, experience: null, urgency: null });
    setTypewriterDone(false);
    setShowContinue(false);
    onClose();
  };

  const goNext = (delay = 0) => {
    setTypewriterDone(false);
    setShowContinue(false);
    if (Math.random() > 0.6 && step > 2) {
      setMicroTrigger(getRandomTrigger());
      setTimeout(() => {
        setMicroTrigger('');
        setTimeout(() => setStep(s => s + 1), 400);
      }, 2500);
    } else {
      setTimeout(() => setStep(s => s + 1), delay);
    }
  };

  const handleAnswer = (key, value) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    updateSession({ [key]: value, step: key });
  };

  const getMirrorText = () => {
    if (answers.experience === 'tried_no_result') return { main: 'Çaba eksik değildi. Yön eksikti.', sub: 'Ve yön olmadan çaba, seni aynı yerde döndürür.' };
    if (answers.experience === 'first_time') return { main: 'Henüz denememiş olman avantajın.', sub: 'Çünkü yanlış yolda mesafe almamışsın.' };
    return { main: 'Bir şeyleri görmüşsün. Bu önemli.', sub: 'Ama görmek tek başına yetmez. Harekete dönüşmesi gerekir.' };
  };

  const getDecisionText = () => {
    const score = (answers.urgency === 'urgent' ? 3 : answers.urgency === 'want_but_delay' ? 2 : 1)
      + (answers.experience === 'tried_no_result' ? 3 : answers.experience === 'tried_some_progress' ? 2 : 1);
    if (score >= 5) return { text: 'Sen bu noktada beklersen… kaybeden yine sen olursun.', tone: 'high' };
    if (score >= 3) return { text: 'Zamanlaman yakın. Ama son adımı atan sensin.', tone: 'mid' };
    return { text: 'Belki şu an için sadece farkında olmak yeterli.', tone: 'low' };
  };

  const handleCTA = () => {
    updateSession({ cta_clicked: true, completed: true });
    localStorage.removeItem('zr_last_step');
    localStorage.removeItem('zr_answers');
    sessionCreated.current = false;
    onClose();
    navigate('/masterclass');
  };

  if (!isOpen) return null;

  // ═══════════════════════════════════════════
  // STEP DEFINITIONS
  // ═══════════════════════════════════════════
  const steps = [
    // ── 0: HOOK ──
    () => (
      <StepContainer depth={0}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
          <motion.div variants={staggerItem}>
            <Typewriter
              text="Zihnin seni buraya getirdi. Sebebi var."
              delay={45}
              className="block text-white/80 text-xl sm:text-2xl font-light font-outfit tracking-wide"
              onComplete={() => setTimeout(() => setShowContinue(true), 1000)}
            />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
              <Whisper>90 saniye. Sadece birkaç soru.</Whisper>
              <Whisper>Sonunda tek bir cevap vereceğim:</Whisper>
              <Divider />
              <Impact>
                Şu an harekete geçmeli misin… yoksa beklemen mi gerekiyor?
              </Impact>
              <motion.div variants={staggerItem}>
                <ContinueBtn onClick={() => goNext()} label="90 Saniye Ver" testId="zr-start" />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 1: NAME ──
    () => (
      <StepContainer depth={0.5}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
          <Whisper>Bir saniye…</Whisper>
          <Impact italic>Seninle gerçekten konuşabilmem için önce bir şeyi bilmem gerek.</Impact>
          {/* [10] Premium name input */}
          <motion.div variants={staggerItem} className="mt-8 relative">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && nameInput.trim()) {
                  setUserName(nameInput.trim());
                  localStorage.setItem('zr_user_name', nameInput.trim());
                  updateSession({ step: 'name' });
                  goNext();
                }
              }}
              placeholder="Adın…"
              className="w-full bg-transparent text-white text-lg sm:text-xl font-light py-4 px-1 focus:outline-none placeholder-white/15 transition-all duration-500 border-none font-outfit tracking-wide"
              autoFocus
              data-testid="zr-name-input"
              style={{ caretColor: 'rgba(168,85,247,0.7)' }}
            />
            {/* Animated underline */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-white/[0.06]" />
            <div
              className={`absolute bottom-0 left-0 h-px transition-all duration-700 ease-out ${nameFocused || nameInput ? 'opacity-100' : 'opacity-0'}`}
              style={{
                width: nameFocused || nameInput ? '100%' : '0%',
                background: 'linear-gradient(to right, rgba(168,85,247,0.5), rgba(236,72,153,0.3), transparent)'
              }}
            />
            {/* Focus glow */}
            {nameFocused && (
              <div className="absolute bottom-0 left-0 right-0 h-[1px] blur-[3px]"
                style={{ background: 'linear-gradient(to right, rgba(168,85,247,0.3), rgba(236,72,153,0.15), transparent)' }}
              />
            )}
          </motion.div>
          {nameInput.trim() && (
            <motion.div
              initial={{ opacity: 0, y: 14, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <ContinueBtn
                onClick={() => {
                  setUserName(nameInput.trim());
                  localStorage.setItem('zr_user_name', nameInput.trim());
                  updateSession({ step: 'name' });
                  goNext();
                }}
                testId="zr-name-next"
              />
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 2: PERMISSION + TENSION ──
    () => (
      <StepContainer depth={1}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
          <Whisper>Şimdi sana birkaç soru soracağım.</Whisper>
          <Whisper>Ama uyarayım… cevaplar rahat ettirmeyebilir.</Whisper>
          <motion.div variants={staggerItem}>
            <SilentPause duration={2000} onComplete={() => setShowContinue(true)} />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
              <Divider />
              <Impact italic>
                Ama eğer hazırsan… başlayalım{userName ? `, ${userName}` : ''}.
              </Impact>
              <motion.div variants={staggerItem}>
                <ContinueBtn onClick={() => goNext()} testId="zr-ready" />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 3: Q1 - Problem ──
    () => (
      <StepContainer depth={3}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
          <Impact>
            {userName ? `${userName}, şu` : 'Şu'} an seni en çok yavaşlatan şey hangisi?
          </Impact>
          <motion.div variants={staggerItem} className="space-y-3 mt-4">
            {[
              { id: 'focus', label: 'Odaklanmak istiyorum ama yapamıyorum' },
              { id: 'money', label: 'Hak ettiğim yerde değilim' },
              { id: 'confidence', label: 'Kendime güvenmek istiyorum ama bir şey engelliyor' },
              { id: 'inner_chaos', label: 'İçimde sürekli bir çatışma var' },
              { id: 'unclear', label: 'Tam olarak ne olduğunu bilmiyorum' },
            ].map(opt => (
              <ChoiceButton
                key={opt.id}
                label={opt.label}
                selected={answers.problem === opt.id}
                onClick={() => handleAnswer('problem', opt.id)}
                testId={`zr-problem-${opt.id}`}
              />
            ))}
          </motion.div>
          {answers.problem && (
            <motion.div variants={staggerItem}>
              <ContinueBtn onClick={() => goNext()} testId="zr-q1-next" />
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 4: Q2 - Experience ──
    () => (
      <StepContainer depth={5}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
          <Impact>
            Daha önce kendin üzerinde çalıştın mı?
          </Impact>
          <motion.div variants={staggerItem} className="space-y-3 mt-4">
            {[
              { id: 'tried_no_result', label: 'Evet ama sonuç alamadım' },
              { id: 'tried_some_progress', label: 'Evet, biraz ilerledim' },
              { id: 'first_time', label: 'Hayır, ilk kez' },
            ].map(opt => (
              <ChoiceButton
                key={opt.id}
                label={opt.label}
                selected={answers.experience === opt.id}
                onClick={() => handleAnswer('experience', opt.id)}
                testId={`zr-exp-${opt.id}`}
              />
            ))}
          </motion.div>
          {answers.experience && (
            <motion.div variants={staggerItem}>
              <ContinueBtn onClick={() => goNext()} testId="zr-q2-next" />
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 5: Q3 - Urgency ──
    () => (
      <StepContainer depth={7}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8">
          <Impact>
            Bugün bu konuda bir adım atmazsan… bir ay sonra ne değişir?
          </Impact>
          <motion.div variants={staggerItem} className="space-y-3 mt-4">
            {[
              { id: 'urgent', label: 'Hiçbir şey. Ve bu beni korkutuyor.' },
              { id: 'want_but_delay', label: 'Değişmek istiyorum ama hep erteliyorum' },
              { id: 'just_looking', label: 'Henüz acil değil, araştırıyorum' },
            ].map(opt => (
              <ChoiceButton
                key={opt.id}
                label={opt.label}
                selected={answers.urgency === opt.id}
                onClick={() => handleAnswer('urgency', opt.id)}
                testId={`zr-urgency-${opt.id}`}
              />
            ))}
          </motion.div>
          {answers.urgency && (
            <motion.div variants={staggerItem}>
              <ContinueBtn onClick={() => goNext()} testId="zr-q3-next" />
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 6: MENTAL MIRROR ──
    () => {
      const mirror = getMirrorText();
      return (
        <StepContainer depth={8}>
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
            <Whisper>{userName ? `${userName}…` : ''} sana bir şey söyleyeceğim.</Whisper>
            <Whisper>Yaşadığın şey bir zayıflık değil.</Whisper>
            <motion.div variants={staggerItem}>
              <SilentPause duration={1500} onComplete={() => setShowContinue(true)} />
            </motion.div>
            {showContinue && (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
                <Whisper>Zihninin seni korumak için kullandığı bir kalkan.</Whisper>
                <Divider />
                <Impact>{mirror.main}</Impact>
                <Impact italic>{mirror.sub}</Impact>
                <motion.div variants={staggerItem}>
                  <ContinueBtn onClick={() => goNext()} testId="zr-mirror-next" />
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </StepContainer>
      );
    },

    // ── 7: DECISION ──
    () => {
      const decision = getDecisionText();
      return (
        <StepContainer depth={9}>
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
            <Whisper>{userName ? `${userName}, herkes` : 'Herkes'} için doğru zaman "şu an" değildir.</Whisper>
            <Whisper>Ve sana "hemen şimdi karar ver" demeyeceğim.</Whisper>
            <motion.div variants={staggerItem}>
              <SilentPause duration={1800} onComplete={() => setShowContinue(true)} />
            </motion.div>
            {showContinue && (
              <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
                <Whisper>Ama şunu bilmeni istiyorum…</Whisper>
                <Divider />
                <Impact className={decision.tone === 'high' ? '' : decision.tone === 'mid' ? 'opacity-70' : 'opacity-50'}>
                  {decision.text}
                </Impact>
                <motion.div variants={staggerItem}>
                  <ContinueBtn onClick={() => goNext()} testId="zr-decision-next" />
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        </StepContainer>
      );
    },

    // ── 8: MASTERCLASS TRANSITION ──
    () => (
      <StepContainer depth={10}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
          <Whisper>Sana 15.000 TL'lik bir süreci önermeyeceğim.</Whisper>
          <Whisper>Henüz değil.</Whisper>
          <motion.div variants={staggerItem}>
            <SilentPause duration={1200} onComplete={() => setShowContinue(true)} />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-7 mt-6">
              <motion.h2 variants={staggerItem} className="text-3xl sm:text-4xl font-semibold text-white font-cormorant tracking-tight">
                Masterclass.
              </motion.h2>
              <Divider />
              <Whisper>3 saat. Tek oturum.</Whisper>
              <Whisper>Çoğu kişi için bu 3 saat… yıllarca erteledikleri kırılma noktası.</Whisper>
              <Whisper>Çünkü ilk kez şunu net görürsün:</Whisper>
              <Impact>
                Zihnin seni nasıl yönlendiriyor… ve sen farkında bile değilsin.
              </Impact>
              {capacity && (
                <motion.div variants={staggerItem} className="flex items-center gap-3 mt-3" data-testid="zr-capacity">
                  <div className="flex-1 h-[3px] bg-white/[0.06] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'linear-gradient(to right, rgba(168,85,247,0.8), rgba(236,72,153,0.6))' }}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((capacity.registered / capacity.max) * 100, 100)}%` }}
                      transition={{ duration: 2, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
                    />
                  </div>
                  <span className="text-white/30 text-[10px] font-light flex-shrink-0 tracking-wide">
                    {capacity.is_full ? 'Bekleme listesi açık' : `Son ${capacity.max - capacity.registered} yer`}
                  </span>
                </motion.div>
              )}
              <motion.div variants={staggerItem} className="pt-3">
                <span className="text-purple-300/40 text-xs font-light tracking-wide">Katılım bedeli: </span>
                <span className="text-white text-2xl font-semibold font-cormorant">3.000 TL</span>
              </motion.div>
              <motion.div variants={staggerItem}>
                <ContinueBtn onClick={() => goNext()} label="Devam Et" testId="zr-mc-next" />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 9: POST-MC INTERVIEW ──
    () => (
      <StepContainer depth={11}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
          <Whisper>Masterclass'tan sonra seninle birebir konuşacağım.</Whisper>
          <motion.div variants={staggerItem}>
            <SilentPause duration={1500} onComplete={() => setShowContinue(true)} />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
              <motion.p variants={staggerItem} className="text-white/60 text-sm font-light font-outfit">15 dakika. Online Zoom görüşmesi.</motion.p>
              <Whisper>Ama bu bir danışmanlık değil…</Whisper>
              <Impact>Seni gerçekten anlama fırsatım.</Impact>
              <Divider />
              <Whisper>Bir sonraki aşamaya herkes geçemiyor.</Whisper>
              <motion.div variants={staggerItem}>
                <ContinueBtn onClick={() => goNext()} testId="zr-interview-next" />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 10: SELECTION ──
    () => (
      <StepContainer depth={11.5}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
          <Whisper>Bu görüşmeden sonra…</Whisper>
          <Impact>Sadece hazır olduğuna inandığım kişilere kapıyı açıyorum.</Impact>
          <motion.div variants={staggerItem}>
            <SilentPause duration={1800} onComplete={() => setShowContinue(true)} />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-5">
              <Whisper>Herkes değil. Herkes olamaz.</Whisper>
              <Whisper>Ama eğer sen o kişiysen…</Whisper>
              <Impact italic>seni içeri alıyorum.</Impact>
              <motion.div variants={staggerItem}>
                <ContinueBtn onClick={() => goNext()} testId="zr-selection-next" />
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),

    // ── 11: MAIN TRAINING ──
    () => (
      <StepContainer depth={12}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-6">
          <Whisper>İçeri alınanlar için… gerçek süreç burada başlar.</Whisper>
          <motion.p variants={staggerItem} className="text-white/60 text-sm font-light font-outfit mt-2">
            3 gün. 9 saat. Birebir.
          </motion.p>
          <Whisper>Burada bilgi aktarılmaz.</Whisper>
          <Impact>Zihnin sökülür… ve yeniden kurulur.</Impact>
          <Divider />
          <motion.div variants={staggerItem} className="space-y-2">
            <span className="text-white/20 text-[10px] font-light tracking-wider block">Masterclass ile birlikte toplam sürec:</span>
            <p className="text-white text-2xl font-semibold font-cormorant">15.000 TL</p>
            <span className="text-white/15 text-[10px] font-light block">Masterclass sonrası kalan: 12.000 TL</span>
          </motion.div>
          <motion.div variants={staggerItem}>
            <ContinueBtn onClick={() => goNext()} testId="zr-training-next" />
          </motion.div>
        </motion.div>
      </StepContainer>
    ),

    // ── 12: REWARD + COMMUNITY ──
    () => (
      <StepContainer depth={12.5}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-7">
          <Whisper>Bu süreci tamamlayanlar:</Whisper>
          <motion.div variants={staggerItem} className="space-y-4 mt-2">
            {['Sertifika ile sürecini belgelendirir', 'Zihne Fısıldayanlar ağına girer — sıradan bir topluluk değil'].map((t, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.6), rgba(236,72,153,0.4))' }} />
                <p className="text-white/60 text-sm font-light font-outfit">{t}</p>
              </div>
            ))}
          </motion.div>
          <Whisper>Ve en önemlisi…</Whisper>
          <Impact>Artık zihinleri onlara itaat eder. Tersi değil.</Impact>
          <Divider />
          <motion.p variants={staggerItem} className="text-white/15 text-[10px] font-light italic font-cormorant tracking-wide leading-relaxed">
            Zihne Fısıldayanlar… bir topluluk değil. Aynı dili konuşan, aynı dönüşümden geçmiş insanların ağı.
          </motion.p>
          <motion.div variants={staggerItem}>
            <ContinueBtn onClick={() => goNext()} testId="zr-community-next" />
          </motion.div>
        </motion.div>
      </StepContainer>
    ),

    // ── 13: FINAL CLOSE ──
    () => (
      <StepContainer depth={13}>
        <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-8 text-center">
          <Whisper className="text-center">Çoğu insan izler…</Whisper>
          <Whisper className="text-center">çok azı gerçekten karar verir.</Whisper>
          <motion.div variants={staggerItem}>
            <SilentPause duration={2500} onComplete={() => setShowContinue(true)} />
          </motion.div>
          {showContinue && (
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-10">
              <Impact className="text-center text-2xl sm:text-3xl">
                Sen hangi taraftasın?
              </Impact>

              {/* Premium CTA */}
              <motion.div variants={staggerItem}>
                <button onClick={handleCTA} className="group relative mx-auto block" data-testid="zr-final-cta">
                  <div className="absolute -inset-[3px] rounded-2xl bg-gradient-to-r from-purple-500/40 via-fuchsia-400/30 to-purple-600/40 animate-ctaPulse blur-md" />
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-purple-500/40 via-fuchsia-400/30 to-purple-600/40" />
                  <div className="relative bg-[#050208]/95 backdrop-blur-2xl px-14 py-6 rounded-2xl overflow-hidden">
                    <div className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-purple-300/20 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent animate-ctaSweep" />
                    <div className="relative flex items-center gap-5">
                      <span className="text-white text-base sm:text-lg font-semibold tracking-widest font-outfit">Masterclass'a Başvur</span>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-fuchsia-500/10 border border-purple-400/20 flex items-center justify-center">
                        <ArrowRight className="w-4 h-4 text-purple-200 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-[5%] right-[5%] h-px bg-gradient-to-r from-transparent via-purple-500/15 to-transparent" />
                  </div>
                </button>
              </motion.div>

              <motion.p variants={staggerItem} className="text-white/[0.06] text-[9px] font-light tracking-wider">
                Bu içerikler kişisel gelişim ve farkındalık odaklıdır. Tıbbi teşhis ve tedavi kapsamında değildir.
              </motion.p>
            </motion.div>
          )}
        </motion.div>
      </StepContainer>
    ),
  ];

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9999] bg-black overflow-y-auto"
          data-testid="zihin-rehberi-overlay"
        >
          {/* [3] Breathing background */}
          <BreathingBackground />

          {/* Header */}
          <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-5">
            <div>
              <span className="text-white/20 text-[10px] font-light tracking-[0.3em] uppercase font-outfit">Zihin Rehberi</span>
              <span className="block text-white/[0.08] text-[9px] font-light italic mt-1 font-cormorant tracking-wide">
                Karar verdiğini sandığın anları… birlikte inceleyelim.
              </span>
            </div>
            <button onClick={handleClose} className="text-white/15 hover:text-white/40 transition-colors duration-500 p-2" data-testid="zr-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* [9] Micro trigger — cinematic vignette overlay */}
          <AnimatePresence>
            {microTrigger && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="fixed inset-0 z-[60] flex items-center justify-center"
                style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.95) 0%, rgba(0,0,0,1) 60%)' }}
              >
                <motion.p
                  initial={{ opacity: 0, scale: 0.92, y: 8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 1.03 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="text-purple-300/50 text-sm font-light italic font-outfit tracking-wider"
                >
                  {microTrigger}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* [1] Cinematic step transitions */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              variants={sceneVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {steps[step] ? steps[step]() : null}
            </motion.div>
          </AnimatePresence>

          {/* Exit Intent — premium glass overlay */}
          <AnimatePresence>
            {showExitIntent && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="fixed inset-0 z-[70] flex items-center justify-center px-6"
                style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.99) 70%)' }}
                data-testid="zr-exit-intent"
              >
                <motion.div
                  initial={{ opacity: 0, y: 24, scale: 0.96, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-md w-full text-center space-y-8"
                >
                  <p className="text-white/25 text-xs font-light tracking-wide font-outfit">Çoğu kişi tam bu noktada geri döner.</p>
                  <p className="text-white text-lg sm:text-xl font-semibold font-outfit leading-relaxed">
                    Ama {userName ? `${userName}…` : ''} sen buraya kadar geldin. Bu tesadüf değil.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <button
                      onClick={() => setShowExitIntent(false)}
                      className="px-10 py-3.5 bg-white/[0.04] border border-purple-500/15 text-white text-sm rounded-xl hover:bg-white/[0.08] hover:border-purple-500/30 transition-all duration-500 font-outfit tracking-wide"
                      data-testid="zr-exit-continue"
                    >
                      Devam Et
                    </button>
                    <button
                      onClick={forceClose}
                      className="px-10 py-3.5 text-white/20 text-sm rounded-xl hover:text-white/40 transition-colors duration-500 font-outfit"
                      data-testid="zr-exit-leave"
                    >
                      Yine de Çık
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Resume Prompt — cinematic */}
          <AnimatePresence>
            {showResumePrompt && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="fixed inset-0 z-[70] flex items-center justify-center px-6"
                style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.98) 0%, black 70%)' }}
                data-testid="zr-resume-prompt"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-md w-full text-center space-y-8"
                >
                  <p className="text-white/50 text-sm font-light italic font-outfit tracking-wide">
                    {userName ? `${userName}…` : ''} yarım kalan bir şey var aramızda.
                  </p>
                  <p className="text-white text-lg sm:text-xl font-semibold font-outfit">
                    Kaldığın yerden devam etmek ister misin?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <button
                      onClick={handleResume}
                      className="px-10 py-3.5 bg-white/[0.04] border border-purple-500/15 text-white text-sm rounded-xl hover:bg-white/[0.08] hover:border-purple-500/30 transition-all duration-500 font-outfit tracking-wide"
                      data-testid="zr-resume-yes"
                    >
                      Devam Et
                    </button>
                    <button
                      onClick={handleFreshStart}
                      className="px-10 py-3.5 text-white/20 text-sm rounded-xl hover:text-white/40 transition-colors duration-500 font-outfit"
                      data-testid="zr-resume-fresh"
                    >
                      Baştan Başla
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
